// @section 1 Business-card field parser - raw OCR text -> structured fields.
//
// Shared by the on-device Tesseract path (primary) and the Azure Vision Read
// backup (Phase 2): both engines hand back raw text, so field extraction lives
// here in one place. High-signal fields (email, phone, website) come out cleanly
// with regex; person name / company / title are heuristic best-guesses that the
// review screen lets the operator correct. This is a pre-fill aid, not the last
// word - imperfect guesses are expected and cheap to fix in two taps.

import type { CardFields } from '../api';

// @subsection 1.1 Patterns
const EMAIL_RE = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/;
// North-American phone shapes: (480) 555-0142, 480-555-0142, 480.555.0142, +1 480 555 0142
const PHONE_RE = /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/;
// A bare domain / URL (subdomains included), not an email. Callers skip lines
// containing '@'. Leading 'www.' is stripped from the captured host.
const URL_RE = /\b((?:[A-Za-z0-9-]+\.)+(?:com|net|org|io|co|biz|us|dev|app|group|health|law|cpa))\b/i;

// Role/title keywords (word-boundary, case-insensitive).
const TITLE_KEYWORDS = [
  'owner', 'founder', 'co-founder', 'president', 'vice president', 'vp', 'ceo', 'cfo', 'coo', 'cto', 'cmo',
  'director', 'managing', 'manager', 'partner', 'principal', 'chief', 'head of', 'lead',
  'engineer', 'architect', 'consultant', 'advisor', 'analyst', 'specialist', 'coordinator',
  'administrator', 'supervisor', 'account executive', 'sales', 'marketing', 'operations',
  'controller', 'accountant', 'attorney', 'agent', 'broker', 'realtor', 'representative', 'associate',
];
// Business-entity suffixes / connective words that mark a company line.
const COMPANY_HINT = /\b(inc|inc\.|llc|l\.l\.c|ltd|co\.|corp|corporation|company|group|associates|partners|services|solutions|systems|holdings|enterprises|industries|consulting|agency|studio|labs|technologies|plumbing|electric|hvac|roofing|construction|contractor|realty|law|cpa|clinic|dental|medical)\b/i;

// @subsection 1.2 Helpers
function cleanLines(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter((l) => l.length > 1);
}

function looksLikePersonName(line: string): boolean {
  if (EMAIL_RE.test(line) || /\d/.test(line)) return false;
  if (COMPANY_HINT.test(line)) return false;
  const words = line.split(' ').filter(Boolean);
  if (words.length < 2 || words.length > 4) return false;
  // Each word starts uppercase (allow initials like "J."). Reject ALL-CAPS blocks
  // (those are usually company logos, e.g. "TESTCO PLUMBING").
  const titleCased = words.filter((w) => /^[A-Z][a-z'.-]+$/.test(w) || /^[A-Z]\.$/.test(w));
  return titleCased.length >= 2;
}

function normalizePhone(s: string): string {
  const m = s.match(PHONE_RE);
  return m ? m[0].trim() : '';
}

// @section 2 Parse
export function parseCardText(raw: string): CardFields {
  const text = raw || '';
  const lines = cleanLines(text);

  const email = (text.match(EMAIL_RE)?.[0] ?? '').toLowerCase() || undefined;

  // Prefer a phone on a line labeled mobile/cell/direct; else the first phone.
  let phone: string | undefined;
  const labeled = lines.find((l) => /\b(mobile|cell|direct|office|tel|phone|p:|m:|o:)\b/i.test(l) && PHONE_RE.test(l));
  phone = normalizePhone(labeled ?? '') || normalizePhone(text) || undefined;

  // Website: a bare domain on a line that is not an email. Strip a leading www.
  let website: string | undefined;
  for (const l of lines) {
    if (EMAIL_RE.test(l)) continue;
    const m = l.match(URL_RE);
    if (m) { website = m[1].toLowerCase().replace(/^www\./, ''); break; }
  }

  // Title: first line containing a role keyword.
  const titleRe = new RegExp(`\\b(${TITLE_KEYWORDS.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`, 'i');
  const title = lines.find((l) => titleRe.test(l) && !EMAIL_RE.test(l)) || undefined;

  // Person name: first line that looks like a name; skip a line we already took as title.
  const nameLine = lines.find((l) => l !== title && looksLikePersonName(l));
  let firstName: string | undefined;
  let lastName: string | undefined;
  if (nameLine) {
    const parts = nameLine.split(' ').filter(Boolean);
    firstName = parts[0];
    lastName = parts.length > 1 ? parts.slice(1).join(' ') : undefined;
  }

  // Company: a line with a business suffix; else the first ALL-CAPS line that is
  // not the person name and is not an email/phone/URL; else derive from the
  // website or email domain (e.g. smithlaw.com -> "Smithlaw").
  let companyName =
    lines.find((l) => l !== nameLine && COMPANY_HINT.test(l) && !EMAIL_RE.test(l)) ||
    lines.find((l) => l !== nameLine && !EMAIL_RE.test(l) && !PHONE_RE.test(l) && !URL_RE.test(l)
      && l === l.toUpperCase() && /[A-Z]{3,}/.test(l));
  if (!companyName) {
    const domain = website ?? (email ? email.split('@')[1] : undefined);
    const base = domain?.replace(/^www\./, '').split('.')[0];
    if (base && base !== 'www') companyName = base.charAt(0).toUpperCase() + base.slice(1);
  }

  return {
    firstName: firstName || null,
    lastName: lastName || null,
    title: title || null,
    companyName: companyName || null,
    email: email || null,
    phone: phone || null,
    website: website || null,
    notes: null,
  };
}

// @section 3 Signal check - did on-device OCR extract enough to skip the cloud backup?
// Used by CardCapture to decide whether the Azure Vision fallback (Phase 2) is
// worth a round-trip: an email or phone plus a name is a solid capture.
export function hasStrongSignal(f: CardFields): boolean {
  const contact = Boolean(f.email || f.phone);
  const identity = Boolean(f.firstName || f.companyName);
  return contact && identity;
}
